import { useCallback, useMemo } from 'react';

import { queryClient } from '../components/queryClient';
import { fleetStages } from '../enums';
import { Authority, EnforcementException, Fleet, FleetConfig } from '../interfaces';
import { deepEqual, deepMerge, getChangedFields, getPreviousValues } from '../utils';

// Fleet-level fields that can be changed (non-config)
interface FleetLevelChanges {
  display_name?: string;
  timezone?: string;
  stage?: fleetStages | null;
  region?: string;
  default_ota_group_id?: number | null;
  logo_url?: string;
  uses_global_routefile?: boolean;
  create_authority?: boolean;
}

// Enum for event type change actions
export enum EventTypeAction {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

// Event type change tracking for Step 4
export interface EventTypeChange {
  action: EventTypeAction;
  value: string; // 'Public' | 'Internal only' | 'None'
  event_type_id: number;
  identifier: string;
}

interface WizardPendingChanges {
  originalConfig: FleetConfig | null;
  accumulatedConfig: Partial<FleetConfig>;
  originalFleetFields: FleetLevelChanges;
  accumulatedFleetFields: FleetLevelChanges;
  // Authorities are tracked separately (not part of config, not part of fleet-level fields)
  originalAuthorities: Authority[] | null;
  accumulatedAuthorities: Authority[] | null;
  // Enforcement exceptions are tracked separately (not part of config)
  originalEnforcementExceptions: EnforcementException[] | null;
  accumulatedEnforcementExceptions: EnforcementException[] | null;
  // Event type changes for Step 4
  eventTypeChanges: Record<number, EventTypeChange>;
  // Logo file to upload (for creation)
  logoFileToUpload?: File | null;
  // Imported authorities (for creation with JSON import)
  importedAuthorities?: Authority[] | null;
}

const QUERY_KEY_BASE = 'wizardPendingChanges';
const NEW_FLEET_KEY = 'new-fleet';

const getQueryKey = (fleetUuid: string) => [QUERY_KEY_BASE, fleetUuid];

/**
 * Helper to preserve previous state while updating specific fields.
 * Provides defaults for all fields to ensure type safety.
 */
const updateState = (prev: WizardPendingChanges | undefined, updates: Partial<WizardPendingChanges>): WizardPendingChanges => ({
  originalConfig: prev?.originalConfig ?? null,
  accumulatedConfig: prev?.accumulatedConfig ?? {},
  originalFleetFields: prev?.originalFleetFields ?? {},
  accumulatedFleetFields: prev?.accumulatedFleetFields ?? {},
  originalAuthorities: prev?.originalAuthorities ?? null,
  accumulatedAuthorities: prev?.accumulatedAuthorities ?? null,
  originalEnforcementExceptions: prev?.originalEnforcementExceptions ?? null,
  accumulatedEnforcementExceptions: prev?.accumulatedEnforcementExceptions ?? null,
  eventTypeChanges: prev?.eventTypeChanges ?? {},
  logoFileToUpload: prev?.logoFileToUpload ?? null,
  importedAuthorities: prev?.importedAuthorities ?? null,
  ...updates,
});

/**
 * Hook to manage pending changes during Fleet Wizard.
 *
 * Supports TWO modes:
 * 1. EDIT MODE: When editing an existing fleet (fleet.uuid exists from API)
 *    - Changes are accumulated in React Query state
 *    - Config changes go through Change Request in Step 8
 *    - Fleet-level/authorities/exceptions are saved directly in Step 8
 *
 * 2. CREATION MODE: When creating a new fleet (no fleet.uuid)
 *    - ALL changes are accumulated in state
 *    - Fleet is created in Step 8 with all accumulated data
 *    - Uses a special "new-fleet" key for state storage
 *
 * @param fleet - Fleet object (may or may not have uuid from API)
 */
export function useWizardPendingChanges(fleet: Partial<Fleet> | null) {
  // Edit mode is determined by whether fleet has a uuid or id from the API
  // If fleet.uuid or fleet.id exists, the fleet was fetched from API = edit mode
  // If neither exists, we're creating a new fleet = creation mode
  const isEditMode = !!fleet?.uuid || !!fleet?.id;
  const isCreationMode = !fleet?.uuid && !fleet?.id;

  // For edit mode, use fleet.uuid; for creation mode, use special "new-fleet" key
  const effectiveKey = fleet?.uuid || NEW_FLEET_KEY;
  const queryKey = useMemo(() => getQueryKey(effectiveKey), [effectiveKey]);

  /**
   * Get current pending changes from React Query state
   */
  const getPendingChanges = useCallback((): WizardPendingChanges | null => {
    return queryClient.getQueryData<WizardPendingChanges>(queryKey) ?? null;
  }, [queryKey]);

  /**
   * Add config changes from a wizard step.
   * Changes are deep merged with existing accumulated changes.
   * @param configChanges - The config changes to add
   * @param explicitOriginalConfig - Optional: explicitly pass the original config (e.g., from freshly fetched fleet data)
   */
  const addConfigChanges = useCallback(
    (configChanges: Partial<FleetConfig>, explicitOriginalConfig?: FleetConfig | null) => {
      queryClient.setQueryData<WizardPendingChanges>(queryKey, (prev) => {
        // Determine original config:
        // 1. If already set in previous state (truthy), keep it
        // 2. Otherwise use explicit original config if provided
        // 3. Otherwise fall back to fleet?.config
        const hasExistingOriginalConfig = prev?.originalConfig && Object.keys(prev.originalConfig).length > 0;
        const originalConfig = hasExistingOriginalConfig
          ? prev.originalConfig
          : (explicitOriginalConfig ?? (fleet?.config as FleetConfig) ?? null);

        // Deep merge the new changes with accumulated changes
        const newAccumulated = deepMerge(
          (prev?.accumulatedConfig ?? {}) as Record<string, unknown>,
          configChanges as Record<string, unknown>,
        );

        return updateState(prev, {
          originalConfig,
          accumulatedConfig: newAccumulated,
          // Initialize original values from fleet on first write (for edit mode comparison)
          originalAuthorities: prev?.originalAuthorities ?? fleet?.authorities ?? null,
          originalEnforcementExceptions: prev?.originalEnforcementExceptions ?? fleet?.enforcement_exceptions ?? null,
        });
      });
    },
    [queryKey, fleet?.config, fleet?.authorities, fleet?.enforcement_exceptions],
  );

  /**
   * Add fleet-level field changes from a wizard step.
   * These are non-config fields like display_name, timezone, stage, etc.
   */
  const addFleetChanges = useCallback(
    (fleetChanges: FleetLevelChanges) => {
      queryClient.setQueryData<WizardPendingChanges>(queryKey, (prev) => {
        const originalFleetFields = prev?.originalFleetFields ?? {
          display_name: fleet?.display_name,
          timezone: fleet?.timezone,
          stage: fleet?.stage,
          region: fleet?.region,
          default_ota_group_id: fleet?.default_ota_group_id,
          logo_url: fleet?.logo_url,
        };

        return updateState(prev, {
          originalConfig: prev?.originalConfig ?? (fleet?.config as FleetConfig) ?? null,
          originalFleetFields,
          accumulatedFleetFields: { ...prev?.accumulatedFleetFields, ...fleetChanges },
          // Initialize original values from fleet on first write (for edit mode comparison)
          originalAuthorities: prev?.originalAuthorities ?? fleet?.authorities ?? null,
          originalEnforcementExceptions: prev?.originalEnforcementExceptions ?? fleet?.enforcement_exceptions ?? null,
        });
      });
    },
    [queryKey, fleet],
  );

  /**
   * Add authorities changes from wizard step 2.
   * Authorities are saved directly (not via CR) but we track them in state
   * so they can be saved together with other changes in Step 8.
   */
  const addAuthoritiesChanges = useCallback(
    (authorities: Authority[]) => {
      queryClient.setQueryData<WizardPendingChanges>(queryKey, (prev) =>
        updateState(prev, {
          originalConfig: prev?.originalConfig ?? (fleet?.config as FleetConfig) ?? null,
          // Initialize original authorities from fleet on first write (for edit mode comparison)
          originalAuthorities: prev?.originalAuthorities ?? fleet?.authorities ?? null,
          accumulatedAuthorities: authorities,
          originalEnforcementExceptions: prev?.originalEnforcementExceptions ?? fleet?.enforcement_exceptions ?? null,
        }),
      );
    },
    [queryKey, fleet],
  );

  /**
   * Add enforcement exceptions changes from wizard step 6.
   * Enforcement exceptions are saved directly (not via CR) but we track them in state
   * so they can be saved together with other changes in Step 8.
   */
  const addEnforcementExceptionsChanges = useCallback(
    (exceptions: EnforcementException[]) => {
      queryClient.setQueryData<WizardPendingChanges>(queryKey, (prev) =>
        updateState(prev, {
          originalConfig: prev?.originalConfig ?? (fleet?.config as FleetConfig) ?? null,
          originalAuthorities: prev?.originalAuthorities ?? fleet?.authorities ?? null,
          // Initialize original exceptions from fleet on first write (for edit mode comparison)
          originalEnforcementExceptions: prev?.originalEnforcementExceptions ?? fleet?.enforcement_exceptions ?? null,
          accumulatedEnforcementExceptions: exceptions,
        }),
      );
    },
    [queryKey, fleet],
  );

  /**
   * Add event type changes from wizard step 4.
   * Changes are stored and applied when fleet is created/updated in Step 8.
   */
  const addEventTypeChanges = useCallback(
    (changes: Record<number, EventTypeChange>) => {
      queryClient.setQueryData<WizardPendingChanges>(queryKey, (prev) =>
        updateState(prev, {
          originalConfig: prev?.originalConfig ?? (fleet?.config as FleetConfig) ?? null,
          originalAuthorities: prev?.originalAuthorities ?? fleet?.authorities ?? null,
          originalEnforcementExceptions: prev?.originalEnforcementExceptions ?? fleet?.enforcement_exceptions ?? null,
          eventTypeChanges: changes,
        }),
      );
    },
    [queryKey, fleet],
  );

  /**
   * Set the logo file to upload (for creation mode).
   */
  const setLogoFileToUpload = useCallback(
    (file: File | null) => {
      queryClient.setQueryData<WizardPendingChanges>(queryKey, (prev) => updateState(prev, { logoFileToUpload: file }));
    },
    [queryKey],
  );

  /**
   * Set imported authorities (from JSON import in creation mode).
   */
  const setImportedAuthorities = useCallback(
    (authorities: Authority[] | null) => {
      queryClient.setQueryData<WizardPendingChanges>(queryKey, (prev) => updateState(prev, { importedAuthorities: authorities }));
    },
    [queryKey],
  );

  /**
   * Get the final merged config (original + accumulated changes).
   * This represents what the config would look like after all changes are applied.
   */
  const getMergedConfig = useCallback((): FleetConfig | null => {
    const pending = getPendingChanges();

    // For creation mode, just return accumulated config
    if (isCreationMode) {
      return pending?.accumulatedConfig as FleetConfig | null;
    }
    if (!pending?.originalConfig) {
      return null;
    }

    return deepMerge(
      pending.originalConfig as unknown as Record<string, unknown>,
      pending.accumulatedConfig as Record<string, unknown>,
    ) as FleetConfig;
  }, [getPendingChanges, isCreationMode]);

  /**
   * Get the diff between original and accumulated changes.
   * Used when creating the Change Request.
   */
  const getChangesDiff = useCallback(() => {
    const pending = getPendingChanges();

    if (!pending?.originalConfig) {
      return { changedFields: {}, previousValues: {} };
    }

    const mergedConfig = deepMerge(
      pending.originalConfig as unknown as Record<string, unknown>,
      pending.accumulatedConfig as Record<string, unknown>,
    );

    const changedFields = getChangedFields(pending.originalConfig as Record<string, unknown>, mergedConfig as Record<string, unknown>);
    const previousValues = getPreviousValues(pending.originalConfig as Record<string, unknown>, changedFields);

    return { changedFields, previousValues };
  }, [getPendingChanges]);

  /**
   * Check if there are any pending config changes
   */
  const hasPendingConfigChanges = useCallback((): boolean => {
    const pending = getPendingChanges();
    if (!pending?.accumulatedConfig) return false;

    return Object.keys(pending.accumulatedConfig).length > 0;
  }, [getPendingChanges]);

  /**
   * Check if there are any pending changes (config, fleet-level, authorities, exceptions, event types, or logo)
   */
  const hasPendingChanges = useCallback((): boolean => {
    const pending = getPendingChanges();
    if (!pending) return false;

    return (
      Object.keys(pending.accumulatedConfig ?? {}).length > 0 ||
      Object.keys(pending.accumulatedFleetFields ?? {}).length > 0 ||
      pending.accumulatedAuthorities !== null ||
      pending.accumulatedEnforcementExceptions !== null ||
      Object.keys(pending.eventTypeChanges ?? {}).length > 0 ||
      pending.logoFileToUpload != null
    );
  }, [getPendingChanges]);

  /**
   * Get the fleet-level fields that have changed and need to be saved directly.
   * Returns only the fields that differ from the original values.
   */
  const getFleetFieldsToSave = useCallback((): FleetLevelChanges => {
    const pending = getPendingChanges();
    if (!pending?.accumulatedFleetFields) return {};

    const { originalFleetFields, accumulatedFleetFields } = pending;
    const changedFields: FleetLevelChanges = {};

    // Only include fields that actually changed from original
    Object.keys(accumulatedFleetFields).forEach((key) => {
      const typedKey = key as keyof FleetLevelChanges;
      const newValue = accumulatedFleetFields[typedKey];
      const origValue = originalFleetFields[typedKey];

      if (newValue !== origValue) {
        (changedFields as Record<string, unknown>)[typedKey] = newValue;
      }
    });

    return changedFields;
  }, [getPendingChanges]);

  /**
   * Get the authorities to save (if changed).
   * Returns the accumulated authorities if they differ from original, otherwise null.
   */
  const getAuthoritiesToSave = useCallback((): Authority[] | null => {
    const pending = getPendingChanges();
    // Use explicit null check - empty array [] is a valid value (clearing all authorities)
    if (pending?.accumulatedAuthorities == null) {
      return null;
    }

    // Compare accumulated vs original - return null if no real change
    if (deepEqual(pending.accumulatedAuthorities, pending.originalAuthorities)) {
      return null;
    }

    return pending.accumulatedAuthorities;
  }, [getPendingChanges]);

  /**
   * Get the enforcement exceptions to save (if changed).
   * Returns the accumulated exceptions if they differ from original, otherwise null.
   */
  const getEnforcementExceptionsToSave = useCallback((): EnforcementException[] | null => {
    const pending = getPendingChanges();
    // Use explicit null check - empty array [] is a valid value (clearing all exceptions)
    if (pending?.accumulatedEnforcementExceptions == null) {
      return null;
    }

    // Compare accumulated vs original - return null if no real change
    if (deepEqual(pending.accumulatedEnforcementExceptions, pending.originalEnforcementExceptions)) {
      return null;
    }

    return pending.accumulatedEnforcementExceptions;
  }, [getPendingChanges]);

  /**
   * Get event type changes for Step 8.
   */
  const getEventTypeChanges = useCallback((): Record<number, EventTypeChange> => {
    const pending = getPendingChanges();
    return pending?.eventTypeChanges ?? {};
  }, [getPendingChanges]);

  /**
   * Get the logo file to upload (for creation mode).
   */
  const getLogoFileToUpload = useCallback((): File | null => {
    const pending = getPendingChanges();
    return pending?.logoFileToUpload ?? null;
  }, [getPendingChanges]);

  /**
   * Get imported authorities (for creation mode with JSON import).
   */
  const getImportedAuthorities = useCallback((): Authority[] | null => {
    const pending = getPendingChanges();
    return pending?.importedAuthorities ?? null;
  }, [getPendingChanges]);

  /**
   * Clear all pending changes (e.g., when wizard is closed or CR is created)
   */
  const clearPendingChanges = useCallback(() => {
    queryClient.removeQueries({ queryKey });
  }, [queryKey]);

  return {
    isEditMode,
    isCreationMode,
    addConfigChanges,
    addFleetChanges,
    addAuthoritiesChanges,
    addEnforcementExceptionsChanges,
    addEventTypeChanges,
    setLogoFileToUpload,
    setImportedAuthorities,
    getMergedConfig,
    getChangesDiff,
    hasPendingChanges,
    hasPendingConfigChanges,
    getFleetFieldsToSave,
    getAuthoritiesToSave,
    getEnforcementExceptionsToSave,
    getEventTypeChanges,
    getLogoFileToUpload,
    getImportedAuthorities,
    clearPendingChanges,
    getPendingChanges,
  };
}

/**
 * Clear pending changes for a specific fleet.
 * Can be called from outside React components.
 */
export function clearWizardPendingChanges(fleetUuid: string) {
  queryClient.removeQueries({ queryKey: getQueryKey(fleetUuid) });
}
